import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Building, Euro, Upload, Save, Loader2, Bell, Mail, Globe } from 'lucide-react';
import { toast } from 'sonner';

export default function InstellingenPage() {
  const { api, user, updateUser } = useAuth();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [autoRemindersEnabled, setAutoRemindersEnabled] = useState(false);
  const [reminderSettings, setReminderSettings] = useState(null);
  const [pendingReminders, setPendingReminders] = useState(null);
  const [processingReminders, setProcessingReminders] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language || 'nl');
  
  const [profileForm, setProfileForm] = useState({
    bedrijfsnaam: '',
    adres: '',
    postcode: '',
    plaats: '',
    telefoon: '',
    kvk_nummer: '',
    btw_nummer: '',
    iban: ''
  });

  const [tariefForm, setTariefForm] = useState({
    standaard_uurtarief: '',
    standaard_km_tarief: '',
    btw_percentage: ''
  });

  useEffect(() => {
    if (user) {
      setProfileForm({
        bedrijfsnaam: user.bedrijfsnaam || '',
        adres: user.adres || '',
        postcode: user.postcode || '',
        plaats: user.plaats || '',
        telefoon: user.telefoon || '',
        kvk_nummer: user.kvk_nummer || '',
        btw_nummer: user.btw_nummer || '',
        iban: user.iban || ''
      });
      setTariefForm({
        standaard_uurtarief: user.standaard_uurtarief?.toString() || '75',
        standaard_km_tarief: user.standaard_km_tarief?.toString() || '0.23',
        btw_percentage: user.btw_percentage?.toString() || '21'
      });
      setAutoRemindersEnabled(user.auto_reminders_enabled || false);
      fetchReminderSettings();
    }
  }, [user]);

  const fetchReminderSettings = async () => {
    try {
      const res = await api.get('/auto-reminders/settings');
      setReminderSettings(res.data);
      const pendingRes = await api.get('/auto-reminders/pending');
      setPendingReminders(pendingRes.data);
    } catch (err) {
      console.error('Kon herinnering instellingen niet laden');
    }
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      const res = await api.put('/auth/profile', profileForm);
      updateUser(res.data);
      toast.success(t('settings.saved'));
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const saveTarieven = async () => {
    setLoading(true);
    try {
      const data = {
        standaard_uurtarief: parseFloat(tariefForm.standaard_uurtarief) || 75,
        standaard_km_tarief: parseFloat(tariefForm.standaard_km_tarief) || 0.23,
        btw_percentage: parseFloat(tariefForm.btw_percentage) || 21
      };
      const res = await api.put('/auth/profile', data);
      updateUser(res.data);
      toast.success(t('settings.saved'));
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('errors.somethingWentWrong'));
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/auth/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      updateUser({ logo_url: res.data.logo_url });
      toast.success(t('settings.saved'));
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const toggleAutoReminders = async (enabled) => {
    try {
      await api.put(`/auto-reminders/settings?enabled=${enabled}`);
      setAutoRemindersEnabled(enabled);
      updateUser({ auto_reminders_enabled: enabled });
      toast.success(t('settings.saved'));
      if (enabled) {
        fetchReminderSettings();
      }
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(t('errors.upgradeRequired'));
      } else {
        toast.error(t('errors.somethingWentWrong'));
      }
    }
  };

  const changeLanguage = async (lang) => {
    try {
      i18n.changeLanguage(lang);
      localStorage.setItem('language', lang);
      setCurrentLang(lang);
      await api.put('/auth/profile', { language: lang });
      updateUser({ language: lang });
      toast.success(t('settings.saved'));
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const processRemindersNow = async () => {
    setProcessingReminders(true);
    try {
      const res = await api.post('/auto-reminders/process');
      if (res.data.sent > 0) {
        toast.success(`${res.data.sent} ${t('settings.pendingReminders', { count: res.data.sent })}`);
      } else {
        toast.info(t('settings.saved'));
      }
      fetchReminderSettings();
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setProcessingReminders(false);
    }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="instellingen-page">
      <div>
        <h1 className="text-2xl font-bold font-heading text-foreground">{t('settings.title')}</h1>
        <p className="text-muted-foreground">{t('settings.subtitle')}</p>
      </div>

      <Tabs defaultValue="bedrijf" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 max-w-lg">
          <TabsTrigger value="bedrijf" data-testid="tab-bedrijf">
            <Building className="w-4 h-4 mr-2" />
            {t('settings.company')}
          </TabsTrigger>
          <TabsTrigger value="tarieven" data-testid="tab-tarieven">
            <Euro className="w-4 h-4 mr-2" />
            {t('settings.rates')}
          </TabsTrigger>
          <TabsTrigger value="herinneringen" data-testid="tab-herinneringen">
            <Bell className="w-4 h-4 mr-2" />
            {t('settings.reminders')}
          </TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account">
            <User className="w-4 h-4 mr-2" />
            {t('settings.account')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bedrijf">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading">{t('settings.companyDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Upload */}
              <div className="space-y-2">
                <Label>{t('settings.logo')}</Label>
                <div className="flex items-center gap-4">
                  {user?.logo_url ? (
                    <img 
                      src={`${backendUrl}${user.logo_url}`} 
                      alt="Logo" 
                      className="w-20 h-20 object-contain border rounded-lg"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Building className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={uploadingLogo}
                    />
                    <Button variant="outline" disabled={uploadingLogo} asChild>
                      <span>
                        {uploadingLogo ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {t('settings.uploadLogo')}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('settings.companyName')}</Label>
                  <Input
                    value={profileForm.bedrijfsnaam}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, bedrijfsnaam: e.target.value }))}
                    placeholder="Jouw Bedrijf B.V."
                    data-testid="settings-bedrijfsnaam"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.phone')}</Label>
                  <Input
                    value={profileForm.telefoon}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, telefoon: e.target.value }))}
                    placeholder="06-12345678"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('settings.address')}</Label>
                <Input
                  value={profileForm.adres}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, adres: e.target.value }))}
                  placeholder="Straatnaam 123"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('settings.postalCode')}</Label>
                  <Input
                    value={profileForm.postcode}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, postcode: e.target.value }))}
                    placeholder="1234 AB"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.city')}</Label>
                  <Input
                    value={profileForm.plaats}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, plaats: e.target.value }))}
                    placeholder="Amsterdam"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('settings.cocNumber')}</Label>
                  <Input
                    value={profileForm.kvk_nummer}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, kvk_nummer: e.target.value }))}
                    placeholder="12345678"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.vatNumber')}</Label>
                  <Input
                    value={profileForm.btw_nummer}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, btw_nummer: e.target.value }))}
                    placeholder="NL123456789B01"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('settings.iban')}</Label>
                <Input
                  value={profileForm.iban}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, iban: e.target.value }))}
                  placeholder="NL12 ABCD 0123 4567 89"
                />
              </div>

              <Button 
                onClick={saveProfile} 
                disabled={loading}
                className="bg-cyan-500 hover:bg-cyan-600"
                data-testid="save-profile-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {t('common.save')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tarieven">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading">{t('settings.defaultRates')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-muted-foreground">
                {t('settings.ratesDescription')}
              </p>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('settings.hourlyRate')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={tariefForm.standaard_uurtarief}
                    onChange={(e) => setTariefForm(prev => ({ ...prev, standaard_uurtarief: e.target.value }))}
                    placeholder="75.00"
                    data-testid="settings-uurtarief"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.kmRate')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={tariefForm.standaard_km_tarief}
                    onChange={(e) => setTariefForm(prev => ({ ...prev, standaard_km_tarief: e.target.value }))}
                    placeholder="0.23"
                    data-testid="settings-kmtarief"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.vatPercentage')}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={tariefForm.btw_percentage}
                    onChange={(e) => setTariefForm(prev => ({ ...prev, btw_percentage: e.target.value }))}
                    placeholder="21"
                    data-testid="settings-btw"
                  />
                </div>
              </div>

              <Button 
                onClick={saveTarieven} 
                disabled={loading}
                className="bg-cyan-500 hover:bg-cyan-600"
                data-testid="save-tarieven-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {t('common.save')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="herinneringen">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading">{t('settings.autoReminders')}</CardTitle>
              <CardDescription>
                {t('settings.autoRemindersDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <Label className="text-base">{t('settings.enableAutoReminders')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.enableAutoRemindersDescription')}
                  </p>
                </div>
                <Switch
                  checked={autoRemindersEnabled}
                  onCheckedChange={toggleAutoReminders}
                  data-testid="auto-reminders-toggle"
                />
              </div>

              {/* Reminder Schedule */}
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {t('settings.reminderSchedule')}
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-yellow-100 text-yellow-700 flex items-center justify-center text-xs font-bold">1</span>
                    <span>{t('settings.reminder1', { days: 7 })}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-xs font-bold">2</span>
                    <span>{t('settings.reminder2', { days: 14 })}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-xs font-bold">3</span>
                    <span>{t('settings.reminder3', { days: 21 })}</span>
                  </div>
                </div>
              </div>

              {/* Pending Reminders */}
              {pendingReminders && pendingReminders.pending_count > 0 && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <h3 className="font-medium mb-2 text-yellow-800 dark:text-yellow-200">
                    {t('settings.pendingReminders', { count: pendingReminders.pending_count })}
                  </h3>
                  <div className="space-y-2 mb-4">
                    {pendingReminders.pending.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="text-sm flex justify-between text-yellow-700 dark:text-yellow-300">
                        <span>{item.invoice_nummer} - {item.client_naam}</span>
                        <span>{item.days_overdue} {t('settings.daysOverdue')}</span>
                      </div>
                    ))}
                  </div>
                  <Button 
                    onClick={processRemindersNow}
                    disabled={processingReminders || !autoRemindersEnabled}
                    variant="outline"
                    className="border-yellow-500 text-yellow-700 hover:bg-yellow-100"
                    data-testid="process-reminders-btn"
                  >
                    {processingReminders ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    {t('settings.sendNow')}
                  </Button>
                </div>
              )}

              {/* Info for free users */}
              {user?.subscription_plan === 'free' && (
                <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    {t('settings.remindersAvailableIn')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="account">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading">{t('settings.account')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Language Selector */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {t('settings.language')}
                </Label>
                <Select value={currentLang} onValueChange={changeLanguage}>
                  <SelectTrigger className="w-48" data-testid="language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nl">🇳🇱 Nederlands</SelectItem>
                    <SelectItem value="en">🇬🇧 English</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t('settings.languageDescription')}</p>
              </div>

              <div className="space-y-2">
                <Label>{t('auth.email')}</Label>
                <Input
                  value={user?.email || ''}
                  disabled
                  className="bg-slate-50"
                />
                <p className="text-xs text-muted-foreground">{t('settings.emailNotEditable')}</p>
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-medium mb-2">{t('settings.currentPlan')}</h3>
                <p className="text-sm">
                  <span className="font-semibold capitalize">{user?.subscription_plan || 'Free'}</span>
                  {user?.subscription_plan === 'free' && (
                    <span className="text-muted-foreground"> - {t('settings.upgradeForMore')}</span>
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
